/**
 * Complete Home Estimation Engine
 * Calculates all materials needed for an entire home based on form inputs
 */

import { CONVERSIONS } from './materialEstimatorData';
import { COST_RATES, HOUSE_TYPES } from './costRates';

// Variance for estimates (±12%)
const ESTIMATE_VARIANCE = 0.12;

/**
 * Calculate with range (min/max based on variance)
 */
const calculateWithRange = (value) => ({
  min: Math.ceil(value * (1 - ESTIMATE_VARIANCE)),
  max: Math.ceil(value * (1 + ESTIMATE_VARIANCE)),
  avg: Math.ceil(value)
});

/**
 * Derive calculation parameters from form data
 */
export const deriveCalculationParams = (formData) => {
  const {
    builtUpArea = 1000,
    floors = 1,
    wallHeight = 10,
    wallType = 'brick45',
    roofType = 'metal',
    ceilingType = 'gypsum',
    foundationType = 'strip',
    finishingLevel = 'standard',
    houseType = 'terrace',
    bedrooms = 3,
    bathrooms = 2,
    kitchen = 1,
    livingArea = 1,
    store = 1,
    mainDoors = 2,
    roomDoors = 5,
    windows = 8
  } = formData;

  const builtUpAreaNum = parseFloat(builtUpArea) || 1000;
  const floorsNum = parseInt(floors) || 1;
  const wallHeightNum = parseFloat(wallHeight) || 10;

  // Calculate derived areas
  const floorArea = builtUpAreaNum; // per floor
  const totalFloorArea = builtUpAreaNum * floorsNum; // all floors
  const roofArea = builtUpAreaNum * 1.15; // with overhang

  // Calculate wall perimeter (approximate from area - assuming roughly square)
  const perimeter = Math.sqrt(builtUpAreaNum) * 4;
  const totalWallLength = perimeter * floorsNum;

  // Internal walls estimation (based on room count)
  const totalRooms = bedrooms + bathrooms + kitchen + livingArea + store;
  const internalWallLength = totalRooms * 20; // ~20ft average internal wall per room
  const combinedWallLength = totalWallLength + internalWallLength;

  const grossWallArea = combinedWallLength * wallHeightNum;

  // Calculate openings
  const mainDoorArea = mainDoors * 21; // 7ft × 3ft
  const roomDoorArea = roomDoors * 17.5; // 7ft × 2.5ft
  const bathroomDoorArea = bathrooms * 14; // 7ft × 2ft
  const windowArea = windows * 12; // 4ft × 3ft
  const totalOpeningsArea = mainDoorArea + roomDoorArea + bathroomDoorArea + windowArea;
  const netWallArea = Math.max(grossWallArea - totalOpeningsArea, 0);

  // Wall tiles area (bathrooms + kitchen splash)
  const wallTileArea = (bathrooms * 80) + (kitchen * 30); // sqft of wall tiles

  // House type multiplier
  const houseTypeData = HOUSE_TYPES[houseType] || HOUSE_TYPES.terrace;

  return {
    floorArea,
    totalFloorArea,
    roofArea,
    netWallArea,
    grossWallArea,
    wallHeight: wallHeightNum,
    wallType,
    roofType,
    ceilingType,
    foundationType,
    floors: floorsNum,
    finishingLevel,
    houseType,
    houseTypeMultiplier: houseTypeData.multiplier,
    perimeter,
    combinedWallLength,
    bedrooms,
    bathrooms,
    kitchen,
    livingArea,
    store,
    mainDoors,
    roomDoors,
    windows,
    totalOpeningsArea,
    wallTileArea,
    builtUpArea: builtUpAreaNum
  };
};

/**
 * Calculate foundation materials
 */
const calculateFoundation = (params) => {
  const { perimeter, floors, foundationType } = params;

  // Foundation length = perimeter + internal load-bearing walls
  const foundationLength = perimeter * 1.3; // 30% extra for internal walls

  // Foundation dimensions based on type
  let widthInch = 18;
  let depthInch = 9;

  if (foundationType === 'pad') {
    // Pad foundation - calculate for columns
    const columnCount = Math.ceil(params.floorArea / 150); // 1 column per 150 sqft
    const padSize = floors > 1 ? 36 : 30; // inches
    const padDepth = 12; // inches

    const lengthM = (padSize * 0.0254);
    const widthM = (padSize * 0.0254);
    const depthM = (padDepth * 0.0254);
    const wetVolume = lengthM * widthM * depthM * columnCount;
    const dryVolume = wetVolume * 1.54;
    const wastage = 1.05;

    // M20: 1:1.5:3
    const cementVolume = (dryVolume / 5.5) * 1 * wastage;
    const sandVolume = (dryVolume / 5.5) * 1.5 * wastage;
    const stoneVolume = (dryVolume / 5.5) * 3 * wastage;

    return {
      cement: Math.ceil(cementVolume * 1440 / 50),
      sand: Math.round(sandVolume * 100) / 100,
      stone: Math.round(stoneVolume * 100) / 100,
      steel: Math.round(wetVolume * 80 * 10) / 10,
      type: 'pad',
      columnCount
    };
  }

  if (foundationType === 'raft') {
    // Raft foundation
    const areaSqm = params.floorArea / CONVERSIONS.SQM_TO_SQFT;
    const thickness = 0.2; // 200mm
    const wetVolume = areaSqm * thickness;
    const dryVolume = wetVolume * 1.54;
    const wastage = 1.05;

    // M20: 1:1.5:3
    const cementVolume = (dryVolume / 5.5) * 1 * wastage;
    const sandVolume = (dryVolume / 5.5) * 1.5 * wastage;
    const stoneVolume = (dryVolume / 5.5) * 3 * wastage;

    return {
      cement: Math.ceil(cementVolume * 1440 / 50),
      sand: Math.round(sandVolume * 100) / 100,
      stone: Math.round(stoneVolume * 100) / 100,
      steel: Math.round(areaSqm * 8 * 10) / 10, // 8 kg/sqm for raft
      type: 'raft'
    };
  }

  // Strip foundation (default)
  const lengthM = foundationLength / CONVERSIONS.M_TO_FT;
  const widthM = (widthInch * 0.0254);
  const depthM = (depthInch * 0.0254);

  const wetVolume = lengthM * widthM * depthM;
  const dryVolume = wetVolume * 1.54;
  const wastage = 1.05;

  // M15: 1:2:4 for strip footing
  const cementVolume = (dryVolume / 7) * 1 * wastage;
  const sandVolume = (dryVolume / 7) * 2 * wastage;
  const stoneVolume = (dryVolume / 7) * 4 * wastage;

  return {
    cement: Math.ceil(cementVolume * 1440 / 50),
    sand: Math.round(sandVolume * 100) / 100,
    stone: Math.round(stoneVolume * 100) / 100,
    steel: Math.round(lengthM * 3 * 10) / 10,
    type: 'strip'
  };
};

/**
 * Calculate structural concrete (columns, beams, ground slab)
 */
const calculateStructure = (params) => {
  const { floorArea, floors, perimeter } = params;

  // Ground slab
  const slabAreaSqm = floorArea / CONVERSIONS.SQM_TO_SQFT;
  const slabThickness = 0.1; // 100mm = 4 inch
  const slabVolume = slabAreaSqm * slabThickness;

  // Columns (9" x 9" x 10ft average)
  const columnCount = Math.ceil(floorArea / 150); // 1 column per 150 sqft
  const columnVolume = columnCount * floors * (0.23 * 0.23 * 3); // 9"x9"x10ft per floor

  // Beams (9" x 12" along perimeter and internal)
  const beamLength = (perimeter + perimeter * 0.3) * floors; // perimeter + 30% internal
  const beamLengthM = beamLength / CONVERSIONS.M_TO_FT;
  const beamVolume = beamLengthM * 0.23 * 0.3; // 9" x 12"

  // Upper floor slabs (if multi-storey)
  const upperSlabVolume = floors > 1 ? slabVolume * (floors - 1) * 0.125 / 0.1 : 0; // 125mm for upper

  const totalConcreteVolume = slabVolume + columnVolume + beamVolume + upperSlabVolume;
  const dryVolume = totalConcreteVolume * 1.54;
  const wastage = 1.05;

  // M20: 1:1.5:3
  const cementVolume = (dryVolume / 5.5) * 1 * wastage;
  const sandVolume = (dryVolume / 5.5) * 1.5 * wastage;
  const stoneVolume = (dryVolume / 5.5) * 3 * wastage;

  // Steel calculation
  const slabSteel = slabAreaSqm * 5 * floors; // 5 kg/sqm
  const columnSteel = columnVolume * 100; // 100 kg/m³
  const beamSteel = beamVolume * 120; // 120 kg/m³

  return {
    cement: Math.ceil(cementVolume * 1440 / 50),
    sand: Math.round(sandVolume * 100) / 100,
    stone: Math.round(stoneVolume * 100) / 100,
    steel: Math.round((slabSteel + columnSteel + beamSteel) * 10) / 10,
    concreteVolume: Math.round(totalConcreteVolume * 100) / 100,
    columnCount
  };
};

/**
 * Calculate wall materials (bricks, plaster, paint)
 */
const calculateWalls = (params) => {
  const { netWallArea, wallType, grossWallArea } = params;

  // Bricks per sqft based on wall type
  let bricksPerSqft = 5; // half brick (4.5")
  if (wallType === 'brick9') {
    bricksPerSqft = 10; // full brick (9")
  } else if (wallType === 'block') {
    bricksPerSqft = 1.125; // concrete blocks (16" x 8")
  }

  const totalBricks = Math.round(netWallArea * bricksPerSqft);
  const bundles = Math.ceil(totalBricks / 600);

  // Cement and sand for brickwork
  const brickworkCement = Math.round(totalBricks * 0.00281 * 10) / 10;
  const brickworkSand = Math.round(totalBricks * 0.0004 * 100) / 100;

  // Plastering (both sides of walls)
  const plasterArea = grossWallArea * 2; // both sides
  const plasterCement = Math.round(plasterArea * 0.01129 * 10) / 10;
  const plasterSand = Math.round(plasterArea * 0.00169 * 100) / 100;

  // Paint (internal only primarily)
  const paintArea = netWallArea * 2; // both sides internal
  const paintLiters = Math.ceil((paintArea * 2) / 100); // 2 coats, 100 sqft/liter
  const primerLiters = Math.ceil(paintLiters * 0.1);

  return {
    bricks: totalBricks,
    bundles,
    brickworkCement,
    brickworkSand,
    plasterCement,
    plasterSand,
    totalCement: brickworkCement + plasterCement,
    totalSand: brickworkSand + plasterSand,
    paintLiters,
    primerLiters,
    wallType
  };
};

/**
 * Calculate flooring materials
 */
const calculateFlooring = (params) => {
  const { totalFloorArea, finishingLevel, wallTileArea } = params;

  // Floor tiles (10% wastage)
  const floorTiles = Math.ceil(totalFloorArea * 1.1);

  // Wall tiles for bathrooms/kitchen
  const wallTiles = Math.ceil(wallTileArea * 1.1);

  // Tile adhesive and grout
  const totalTileArea = floorTiles + wallTiles;
  const adhesiveBags = Math.ceil((totalTileArea * 0.05) / 20); // 20kg bags
  const grout = Math.ceil(totalTileArea * 0.02); // kg

  // Floor screed (25mm thickness)
  const screedAreaSqm = totalFloorArea / CONVERSIONS.SQM_TO_SQFT;
  const screedVolume = screedAreaSqm * 0.025 * 1.1; // 25mm with 10% wastage
  const screedCement = Math.ceil(screedVolume * (1/4) * 1440 / 50);
  const screedSand = Math.round(screedVolume * (3/4) * 1.2 * 100) / 100;

  return {
    floorTiles,
    wallTiles,
    totalTiles: floorTiles + wallTiles,
    adhesiveBags,
    grout,
    screedCement,
    screedSand
  };
};

/**
 * Calculate roofing materials
 */
const calculateRoofing = (params) => {
  const { roofArea, roofType, floorArea } = params;

  if (roofType === 'metal') {
    // Metal deck sheets with 15% overlap
    const sheets = Math.ceil(roofArea * 1.15);
    const screws = Math.ceil(roofArea * 0.5);
    return {
      type: 'metal',
      sheets,
      screws,
      sheetsSqft: sheets
    };
  }

  if (roofType === 'concrete') {
    // Concrete roof slab (125mm)
    const areaSqm = roofArea / CONVERSIONS.SQM_TO_SQFT;
    const thickness = 0.125; // 125mm
    const wetVolume = areaSqm * thickness;
    const dryVolume = wetVolume * 1.54;
    const wastage = 1.05;

    // M20: 1:1.5:3
    const cementVolume = (dryVolume / 5.5) * 1 * wastage;
    const sandVolume = (dryVolume / 5.5) * 1.5 * wastage;
    const stoneVolume = (dryVolume / 5.5) * 3 * wastage;

    return {
      type: 'concrete',
      cement: Math.ceil(cementVolume * 1440 / 50),
      sand: Math.round(sandVolume * 100) / 100,
      stone: Math.round(stoneVolume * 100) / 100,
      steel: Math.round(areaSqm * 6 * 10) / 10
    };
  }

  // Clay/Concrete tiles
  const tilesCount = Math.ceil(roofArea * 0.12 * 1.1); // ~12 tiles per sqft with wastage
  return {
    type: 'tiles',
    tiles: tilesCount,
    battens: Math.ceil(roofArea * 0.3) // linear ft of battens
  };
};

/**
 * Calculate ceiling materials
 */
const calculateCeiling = (params) => {
  const { totalFloorArea, ceilingType } = params;

  if (ceilingType === 'gypsum') {
    // Standard board: 4ft x 8ft = 32 sqft
    const boards = Math.ceil((totalFloorArea * 1.1) / 32);
    const screws = Math.ceil(totalFloorArea * 1.5);
    const compound = Math.round(boards * 0.5 * 10) / 10;
    const furring = Math.ceil(totalFloorArea / 4); // metal furring channels

    return {
      type: 'gypsum',
      boards,
      screws,
      compound,
      furring
    };
  }

  if (ceilingType === 'pvc') {
    const panels = Math.ceil(totalFloorArea * 1.1);
    const trim = Math.ceil(Math.sqrt(totalFloorArea) * 4 * 1.1);

    return {
      type: 'pvc',
      panels,
      trim
    };
  }

  // Plaster ceiling
  const areaSqm = totalFloorArea / CONVERSIONS.SQM_TO_SQFT;
  const plasterCement = Math.round(totalFloorArea * 0.01129 * 10) / 10;
  const plasterSand = Math.round(totalFloorArea * 0.00169 * 100) / 100;

  return {
    type: 'plaster',
    cement: plasterCement,
    sand: plasterSand
  };
};

/**
 * Calculate M&E rough estimates
 */
const calculateME = (params) => {
  const { totalFloorArea, bathrooms, bedrooms, kitchen, livingArea } = params;
  const totalRooms = bathrooms + bedrooms + kitchen + livingArea;

  // Electrical points estimate
  const lightPoints = Math.ceil(totalFloorArea / 50); // 1 per 50 sqft
  const socketPoints = totalRooms * 3; // 3 per room average
  const fanPoints = totalRooms;

  // Plumbing points
  const waterPoints = bathrooms * 4 + kitchen * 2; // taps, showers, WC
  const drainPoints = bathrooms * 2 + kitchen * 1;

  return {
    lightPoints,
    socketPoints,
    fanPoints,
    waterPoints,
    drainPoints,
    totalElectricalPoints: lightPoints + socketPoints + fanPoints
  };
};

/**
 * Aggregate all materials
 */
const aggregateMaterials = (results) => {
  const { foundation, structure, walls, flooring, roofing, ceiling } = results;

  // Aggregate cement
  let totalCement = foundation.cement + structure.cement + walls.totalCement + flooring.screedCement;
  if (roofing.type === 'concrete') {
    totalCement += roofing.cement;
  }
  if (ceiling.type === 'plaster') {
    totalCement += ceiling.cement;
  }

  // Aggregate sand (fine + coarse)
  let sandFine = walls.totalSand + flooring.screedSand;
  let sandCoarse = foundation.sand + structure.sand;
  if (roofing.type === 'concrete') {
    sandCoarse += roofing.sand;
  }
  if (ceiling.type === 'plaster') {
    sandFine += ceiling.sand;
  }

  // Aggregate stone/aggregate
  let stone = foundation.stone + structure.stone;
  if (roofing.type === 'concrete') {
    stone += roofing.stone;
  }

  // Aggregate steel
  let steel = foundation.steel + structure.steel;
  if (roofing.type === 'concrete') {
    steel += roofing.steel;
  }

  return {
    cement: {
      total: totalCement,
      unit: 'bags (50kg)',
      unitMy: 'beg (50kg)'
    },
    sandFine: {
      total: Math.round(sandFine * 100) / 100,
      unit: 'm³',
      unitMy: 'm³',
      tons: Math.round(sandFine * 1.5 * 100) / 100
    },
    sandCoarse: {
      total: Math.round(sandCoarse * 100) / 100,
      unit: 'm³',
      unitMy: 'm³',
      tons: Math.round(sandCoarse * 1.6 * 100) / 100
    },
    stone: {
      total: Math.round(stone * 100) / 100,
      unit: 'm³',
      unitMy: 'm³',
      tons: Math.round(stone * 1.5 * 100) / 100
    },
    steel: {
      total: Math.round(steel),
      unit: 'kg',
      unitMy: 'kg',
      tons: Math.round(steel / 1000 * 100) / 100
    },
    bricks: {
      total: walls.bricks,
      unit: 'pcs',
      unitMy: 'biji',
      bundles: walls.bundles
    }
  };
};

/**
 * Calculate costs by category
 */
const calculateCosts = (results, params) => {
  const { finishingLevel, totalFloorArea, houseTypeMultiplier } = params;
  const rates = COST_RATES[finishingLevel] || COST_RATES.standard;

  // Base cost calculation
  const baseCostPerSqft = (rates.min + rates.max) / 2;
  const adjustedCostPerSqft = baseCostPerSqft * houseTypeMultiplier;
  const baseTotalCost = totalFloorArea * adjustedCostPerSqft;

  // Category breakdown percentages
  const categories = {
    foundation: { percentage: 12, label: 'Foundation', labelMy: 'Asas' },
    structure: { percentage: 22, label: 'Structure', labelMy: 'Struktur' },
    walls: { percentage: 17, label: 'Walls & Finish', labelMy: 'Dinding & Kemasan' },
    flooring: { percentage: 14, label: 'Flooring', labelMy: 'Lantai' },
    roofing: { percentage: 16, label: 'Roofing', labelMy: 'Bumbung' },
    me: { percentage: 12, label: 'M&E Works', labelMy: 'Kerja M&E' },
    others: { percentage: 7, label: 'Others', labelMy: 'Lain-lain' }
  };

  const categoryBreakdown = Object.entries(categories).map(([key, data]) => ({
    id: key,
    label: data.label,
    labelMy: data.labelMy,
    percentage: data.percentage,
    amount: Math.round(baseTotalCost * (data.percentage / 100))
  }));

  return {
    totalCost: {
      min: Math.round(totalFloorArea * rates.min * houseTypeMultiplier),
      max: Math.round(totalFloorArea * rates.max * houseTypeMultiplier),
      avg: Math.round(baseTotalCost)
    },
    perSqft: {
      min: Math.round(rates.min * houseTypeMultiplier),
      max: Math.round(rates.max * houseTypeMultiplier)
    },
    categories: categoryBreakdown
  };
};

/**
 * Main calculation function - calculates all materials for complete home
 */
export const calculateCompleteHome = (formData) => {
  // Derive all parameters
  const params = deriveCalculationParams(formData);

  // Calculate each category
  const foundation = calculateFoundation(params);
  const structure = calculateStructure(params);
  const walls = calculateWalls(params);
  const flooring = calculateFlooring(params);
  const roofing = calculateRoofing(params);
  const ceiling = calculateCeiling(params);
  const me = calculateME(params);

  const categoryResults = {
    foundation,
    structure,
    walls,
    flooring,
    roofing,
    ceiling,
    me
  };

  // Aggregate materials
  const aggregated = aggregateMaterials(categoryResults);

  // Calculate costs
  const costs = calculateCosts(categoryResults, params);

  // Build detailed materials list with ranges
  const materials = {
    concrete: {
      cement: {
        ...calculateWithRange(aggregated.cement.total),
        unit: aggregated.cement.unit,
        unitMy: aggregated.cement.unitMy,
        label: 'Cement',
        labelMy: 'Simen'
      },
      concreteVolume: {
        ...calculateWithRange(structure.concreteVolume),
        unit: 'm³',
        unitMy: 'm³',
        label: 'Ready-Mix Concrete',
        labelMy: 'Konkrit Siap Campur'
      }
    },
    aggregates: {
      sandFine: {
        ...calculateWithRange(aggregated.sandFine.total),
        unit: 'm³',
        unitMy: 'm³',
        label: 'Sand (fine)',
        labelMy: 'Pasir (halus)',
        tons: calculateWithRange(aggregated.sandFine.tons)
      },
      sandCoarse: {
        ...calculateWithRange(aggregated.sandCoarse.total),
        unit: 'm³',
        unitMy: 'm³',
        label: 'Sand (coarse)',
        labelMy: 'Pasir (kasar)',
        tons: calculateWithRange(aggregated.sandCoarse.tons)
      },
      stone: {
        ...calculateWithRange(aggregated.stone.total),
        unit: 'm³',
        unitMy: 'm³',
        label: 'Stone/Gravel',
        labelMy: 'Batu/Kerikil',
        tons: calculateWithRange(aggregated.stone.tons)
      }
    },
    bricks: {
      bricks: {
        ...calculateWithRange(aggregated.bricks.total),
        unit: 'pcs',
        unitMy: 'biji',
        label: 'Bricks',
        labelMy: 'Bata',
        bundles: calculateWithRange(aggregated.bricks.bundles)
      }
    },
    steel: {
      reinforcement: {
        ...calculateWithRange(aggregated.steel.total),
        unit: 'kg',
        unitMy: 'kg',
        label: 'Steel Reinforcement',
        labelMy: 'Tetulang Keluli',
        tons: calculateWithRange(aggregated.steel.tons)
      }
    },
    finishing: {
      floorTiles: {
        ...calculateWithRange(flooring.floorTiles),
        unit: 'sqft',
        unitMy: 'kaki persegi',
        label: 'Floor Tiles',
        labelMy: 'Jubin Lantai'
      },
      wallTiles: {
        ...calculateWithRange(flooring.wallTiles),
        unit: 'sqft',
        unitMy: 'kaki persegi',
        label: 'Wall Tiles',
        labelMy: 'Jubin Dinding'
      },
      paint: {
        ...calculateWithRange(walls.paintLiters),
        unit: 'liters',
        unitMy: 'liter',
        label: 'Paint (emulsion)',
        labelMy: 'Cat (emulsi)'
      },
      primer: {
        ...calculateWithRange(walls.primerLiters),
        unit: 'liters',
        unitMy: 'liter',
        label: 'Primer',
        labelMy: 'Pendasar'
      }
    },
    roofing: roofing.type === 'metal' ? {
      sheets: {
        ...calculateWithRange(roofing.sheets),
        unit: 'sqft',
        unitMy: 'kaki persegi',
        label: 'Metal Deck Sheets',
        labelMy: 'Kepingan Dek Logam'
      },
      screws: {
        ...calculateWithRange(roofing.screws),
        unit: 'pcs',
        unitMy: 'biji',
        label: 'Roofing Screws',
        labelMy: 'Skru Bumbung'
      }
    } : roofing.type === 'tiles' ? {
      tiles: {
        ...calculateWithRange(roofing.tiles),
        unit: 'pcs',
        unitMy: 'keping',
        label: 'Roof Tiles',
        labelMy: 'Jubin Bumbung'
      }
    } : {
      // concrete roof materials already in aggregated
    },
    ceiling: ceiling.type === 'gypsum' ? {
      boards: {
        ...calculateWithRange(ceiling.boards),
        unit: 'sheets (4\'×8\')',
        unitMy: 'keping (4\'×8\')',
        label: 'Gypsum Boards',
        labelMy: 'Papan Gipsum'
      },
      furring: {
        ...calculateWithRange(ceiling.furring),
        unit: 'pcs',
        unitMy: 'batang',
        label: 'Metal Furring',
        labelMy: 'Furring Logam'
      }
    } : ceiling.type === 'pvc' ? {
      panels: {
        ...calculateWithRange(ceiling.panels),
        unit: 'sqft',
        unitMy: 'kaki persegi',
        label: 'PVC Panels',
        labelMy: 'Panel PVC'
      }
    } : {}
  };

  return {
    summary: costs,
    materials,
    categoryResults,
    inputs: formData,
    params,
    generatedAt: new Date().toISOString()
  };
};

export default calculateCompleteHome;
